import React, { useContext } from 'react';
import './Wishlist.css';
import { LocaleContext, AuthContext, WishlistContext, CartContext } from '../locale';

function WishlistItem({ item, removeFromWishlist, addToCart, isInCart, t, locale }) {
  const nf = new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: 'EUR' });
  const emoji = item.category === 'beer' ? '🍺' : '🍷';

  return (
    <div className="wishlist-item-card">
      <div className="item-visual">
        <div className={`product-image ${item.category}`}>
          {item.image_url ? (
            <img 
              src={item.image_url} 
              alt={item.name}
              className="wishlist-product-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <span 
            className="product-emoji" 
            style={{display: item.image_url ? 'none' : 'flex'}}
          >
            {emoji}
          </span>
        </div>
      </div>
      
      <div className="item-details">
        <div className="item-header">
          <h3 className="item-name">{item.name}</h3>
          <div className="item-category">{item.category === 'beer' ? 'Bière' : 'Vin'}</div>
        </div>
        
        <div className="item-meta">
          <span className="brand">{item.marque}</span>
          <span className="separator">•</span>
          <span className="volume">{item.volume}</span>
          <span className="separator">•</span>
          <span className="abv">{item.abv}% vol</span>
        </div>
        
        <div className="item-description">
          <span className="type">{item.type}</span>
          <span className="separator">•</span>
          <span className="country">{item.country}</span>
          <span className="separator">•</span>
          <span className="gamme">{item.gamme}</span>
        </div>
        
        <div className="item-price">{nf.format(item.price)}</div>
      </div>
      
      <div className="item-actions">
        <button 
          className={`action-btn primary ${isInCart(item.id) ? 'in-cart' : ''}`}
          onClick={() => addToCart(item)}
          disabled={isInCart(item.id)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="m1 1 4 4 2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          {isInCart(item.id) ? t('common.inCart') : t('common.addToCart')}
        </button>
        
        <button 
          className="action-btn secondary remove"
          onClick={() => removeFromWishlist(item.id)}
          title={t('common.removeFromWishlist')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>
  );
}

function EmptyWishlist({ t }) {
  return (
    <div className="empty-wishlist">
      <div className="empty-icon">
        <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
        </svg>
      </div>
      <h3>{t('common.emptyWishlist')}</h3>
      <p>Découvrez nos produits et ajoutez vos favoris ici</p>
      <a href="#/products" className="btn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: 8}}>
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M16 12l-4-4-4 4M12 16V8"></path>
        </svg>
        Voir les produits
      </a>
    </div>
  );
}

export default function Wishlist() {
  const { t, locale } = useContext(LocaleContext);
  const { user, openAuth } = useContext(AuthContext);
  const { wishlist, removeFromWishlist, loading } = useContext(WishlistContext);
  const { addToCart, isInCart } = useContext(CartContext);

  const handleAddToCart = (product, qty = 1) => {
    if (!user) {
      openAuth('login');
      return;
    }
    addToCart({ ...product, quantity: qty }, qty);
  };

  const handleRemoveFromWishlist = (productId) => {
    removeFromWishlist(productId);
  };

  // Show loading state while fetching wishlist
  if (loading) {
    return (
      <section className="wishlist-page">
        <div className="container">
          <div className="page-header">
            <div className="header-content">
              <div className="header-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </div>
              <div className="header-text">
                <h1 className="h1">{t('nav.wishlist')}</h1>
                <p className="page-subtitle">Chargement...</p>
              </div>
            </div>
          </div>
          
          <div className="wishlist-content">
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '60px 0',
              color: 'var(--muted)'
            }}>
              <div style={{
                width: 40,
                height: 40,
                border: '3px solid rgba(212,154,106,0.3)',
                borderTop: '3px solid var(--accent)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: 16
              }} />
              Chargement de votre liste de favoris...
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="wishlist-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <div className="header-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
            </div>
            <div className="header-text">
              <h1 className="h1">{t('nav.wishlist')}</h1>
              <p className="page-subtitle">
                {wishlist.length > 0 
                  ? `${wishlist.length} produit${wishlist.length > 1 ? 's' : ''} dans votre liste de favoris`
                  : 'Votre liste de favoris'
                }
              </p>
            </div>
          </div>
          
          {wishlist.length > 0 && (
            <div className="header-actions">
              <button 
                className="btn secondary"
                onClick={() => {
                  if (!user) {
                    openAuth('login');
                    return;
                  }
                  wishlist.forEach(item => {
                    if (!isInCart(item.id)) {
                      // ensure quantity=1 is passed for bulk add
                      addToCart({ ...item, quantity: 1 }, 1);
                    }
                  });
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: 8}}>
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="m1 1 4 4 2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
                Tout ajouter au panier
              </button>
            </div>
          )}
        </div>

        <div className="wishlist-content">
          {wishlist.length === 0 ? (
            <EmptyWishlist t={t} />
          ) : (
            <div className="wishlist-grid">
              {wishlist.map(item => (
                <WishlistItem
                  key={item.id}
                  item={item}
                  removeFromWishlist={handleRemoveFromWishlist}
                  addToCart={(p) => handleAddToCart(p, 1)}
                  isInCart={isInCart}
                  t={t}
                  locale={locale}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
